// Gedeelde helpers voor de onboarding-wizard.
// Seeds komen uit data/persoondata.txt. Zolang /inbox die nog niet naar D1 heeft
// geschreven, kan de gebruiker met de "Vul Rahiem's gegevens in"-knoppen zichzelf
// uit deze constants seeden.

export const RAHIEM_SEED = {
  voornaam: 'Rahiem',
  achternaam: 'Bechan',
  roepnaam: 'Rahiem',
  birth_date: '2000-03-24',
  birth_place: "'s-Gravenhage",
  birth_country: 'Nederland',
  phone: '0627229977',
  bsn: '237034438',
  address_street: 'Nimfkruid 13',
  address_postcode: '2498 EX',
  address_city: "'s-Gravenhage",
};

export const RAHIEM_TRIGION_SEED = {
  personeelsnummer: '10056759',
  datum_in_dienst: '2025-04-07',
  manager_naam: 'Leeuwen S. van (Sven)',
  praktijkopleider_naam: 'Mike Makkelie',
  afdeling: 'Mdw Rayon 303 Team 15',
  plangebied: 'Zuid-West',
  kostenplaats: 'ZuidWest 303',
  functiecode: '2500-0003',
  studentnummer: '4120197',
  profitcenter: '0002500303',
};

export interface OnboardingData {
  naw?: {
    voornaam?: string;
    achternaam?: string;
    roepnaam?: string | null;
  };
  contact?: {
    phone?: string | null;
    birth_place?: string | null;
    birth_country?: string | null;
  };
  sensitive?: {
    bsn?: string | null;
    birth_date?: string | null;
    address_street?: string | null;
    address_postcode?: string | null;
    address_city?: string | null;
  };
  werkgever?: {
    personeelsnummer?: string;
    datum_in_dienst?: string;
    manager_naam?: string | null;
    praktijkopleider_naam?: string | null;
    afdeling?: string | null;
    plangebied?: string | null;
    kostenplaats?: string | null;
    functiecode?: string | null;
    studentnummer?: string | null;
    profitcenter?: string | null;
  };
  iban?: {
    iban?: string | null;
  };
  meta?: {
    persoondata_imported?: boolean;
  };
}

export type StepKey =
  | 'welcome'
  | 'naw'
  | 'contact'
  | 'sensitive'
  | 'werkgever'
  | 'contract'
  | 'objects'
  | 'diplomas'
  | 'salary'
  | 'iban'
  | 'preferences'
  | 'summary';

export interface StepDef {
  id: number;
  key: StepKey;
  title: string;
  subtitle?: string;
}

export const STEPS: StepDef[] = [
  { id: 1, key: 'welcome', title: 'Welkom' },
  { id: 2, key: 'naw', title: 'Naam', subtitle: 'Hoe je heet en hoe je genoemd wilt worden.' },
  { id: 3, key: 'contact', title: 'Contact', subtitle: 'Telefoonnummer en geboorteplaats.' },
  { id: 4, key: 'sensitive', title: 'Persoonsgegevens', subtitle: 'BSN, geboortedatum en adres.' },
  { id: 5, key: 'werkgever', title: 'Werkgever', subtitle: 'Je gegevens bij Trigion.' },
  { id: 6, key: 'contract', title: 'Contract', subtitle: 'Je arbeidsovereenkomst.' },
  { id: 7, key: 'objects', title: 'Werklocaties', subtitle: 'De objecten waar je werkt.' },
  { id: 8, key: 'diplomas', title: "Diploma's", subtitle: 'Je opleidingen en certificaten.' },
  { id: 9, key: 'salary', title: 'Loon', subtitle: 'Schaal, trede en uurloon.' },
  { id: 10, key: 'iban', title: 'Bankrekening', subtitle: 'Waar je loon op binnenkomt.' },
  { id: 11, key: 'preferences', title: 'Voorkeuren', subtitle: 'Taal, thema en notatie.' },
  { id: 12, key: 'summary', title: 'Controleren', subtitle: 'Kijk je gegevens na en ga naar de app.' },
];

// ---- validators ------------------------------------------------------------

export interface ValidationResult {
  ok: boolean;
  errors: Record<string, string>;
}

const OK: ValidationResult = { ok: true, errors: {} };

function fail(errors: Record<string, string>): ValidationResult {
  return { ok: false, errors };
}

/** BSN elfproef: som(pos*waarde) met laatste cijfer -1, mod 11 == 0 */
export function isValidBsn(bsn: string): boolean {
  const digits = bsn.replace(/\D/g, '');
  if (digits.length < 8 || digits.length > 9) return false;
  const padded = digits.padStart(9, '0');
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const n = Number(padded[i]);
    if (Number.isNaN(n)) return false;
    sum += i === 8 ? -n : n * (9 - i);
  }
  return sum % 11 === 0;
}

/** IBAN mod-97 check — vormt landcode+controlegetal achteraan, mapt letters naar cijfers. */
export function isValidIban(raw: string): boolean {
  const iban = raw.replace(/\s+/g, '').toUpperCase();
  if (iban.length < 15 || iban.length > 34) return false;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const expanded = rearranged
    .split('')
    .map((ch) => (/[A-Z]/.test(ch) ? (ch.charCodeAt(0) - 55).toString() : ch))
    .join('');
  // Big-number mod-97 via chunked reduction
  let remainder = 0;
  for (let i = 0; i < expanded.length; i += 7) {
    const chunk = remainder.toString() + expanded.slice(i, i + 7);
    remainder = Number(chunk) % 97;
  }
  return remainder === 1;
}

export function isValidDutchPhone(raw: string | null | undefined): boolean {
  if (!raw) return false;
  const d = raw.replace(/[\s-]/g, '');
  return /^(\+31|0031|0)[1-9]\d{8}$/.test(d);
}

export function isValidDutchPostcode(raw: string | null | undefined): boolean {
  if (!raw) return false;
  return /^\d{4}\s?[A-Za-z]{2}$/.test(raw.trim());
}

export function normalizeDutchPostcode(raw: string): string {
  const m = raw.trim().match(/^(\d{4})\s*([A-Za-z]{2})$/);
  if (!m) return raw.trim().toUpperCase();
  return `${m[1]} ${m[2].toUpperCase()}`;
}

export function validateStep(key: StepKey, data: OnboardingData): ValidationResult {
  switch (key) {
    case 'welcome':
      return OK;
    case 'naw': {
      const d = data.naw ?? {};
      const e: Record<string, string> = {};
      if (!d.voornaam?.trim()) e.voornaam = 'Voornaam is verplicht';
      if (!d.achternaam?.trim()) e.achternaam = 'Achternaam is verplicht';
      return Object.keys(e).length ? fail(e) : OK;
    }
    case 'contact': {
      const d = data.contact ?? {};
      const e: Record<string, string> = {};
      if (d.phone && !isValidDutchPhone(d.phone)) e.phone = 'Vul een geldig Nederlands telefoonnummer in, bijvoorbeeld 0612345678';
      return Object.keys(e).length ? fail(e) : OK;
    }
    case 'sensitive': {
      const d = data.sensitive ?? {};
      const e: Record<string, string> = {};
      if (d.bsn && !isValidBsn(d.bsn)) e.bsn = 'Dit BSN-nummer voldoet niet aan de elfproef';
      if (d.address_postcode && !isValidDutchPostcode(d.address_postcode)) e.address_postcode = 'Gebruik het formaat 1234 AB';
      if (d.birth_date && !/^\d{4}-\d{2}-\d{2}$/.test(d.birth_date)) e.birth_date = 'Ongeldige datum';
      return Object.keys(e).length ? fail(e) : OK;
    }
    case 'werkgever': {
      const d = data.werkgever ?? {};
      const e: Record<string, string> = {};
      if (!d.personeelsnummer?.trim()) e.personeelsnummer = 'Personeelsnummer is verplicht';
      if (!d.datum_in_dienst?.trim()) e.datum_in_dienst = 'Datum in dienst is verplicht';
      return Object.keys(e).length ? fail(e) : OK;
    }
    case 'iban': {
      const d = data.iban ?? {};
      const e: Record<string, string> = {};
      if (d.iban && !isValidIban(d.iban)) e.iban = 'Dit IBAN klopt niet';
      return Object.keys(e).length ? fail(e) : OK;
    }
    default:
      return OK;
  }
}

/**
 * Detecteert of persoondata.txt al door /inbox naar D1 is geschreven.
 * We gebruiken als heuristiek: profile.bsn en profile.personeelsnummer zijn beide
 * gevuld. Zolang beide leeg zijn, tonen we auto-fill-knoppen zodat Rahiem direct
 * kan starten zonder te wachten op /inbox.
 */
export function hasPersoondataImported(profile: any): boolean {
  if (!profile) return false;
  return !!(profile.bsn && profile.personeelsnummer);
}

/**
 * Haal de te-patchen velden uit de OnboardingData. We mappen naar de velden die
 * PATCH /api/profile accepteert (zie worker/src/routes/profile.ts).
 */
export function collectProfilePatch(data: OnboardingData): Record<string, any> {
  const out: Record<string, any> = {};
  const copy = (src: any) => {
    if (!src) return;
    for (const [k, v] of Object.entries(src)) {
      if (v === undefined) continue;
      out[k] = v === '' ? null : v;
    }
  };
  copy(data.naw);
  copy(data.contact);
  copy(data.sensitive);
  copy(data.werkgever);
  copy(data.iban);
  return out;
}
